import React from 'react';
import PropTypes from 'prop-types';
import { useHistory, withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import classNames from 'classnames';

import ProLayout from '../../../../components/ProLayout';
import useBasename from '../../../../../../components/routing/useBasename';
import Button from '../../../../../../components/global/Button';
import { AlbumFolderForContributor } from '../../../../../../graphql/queries/album-folder';
import useIsNativeWebView from '../../../../../../services/native-bridge/hooks/useIsWebView';
import { MeQuery } from '../../../../../../graphql/queries/account';
import useAlbumMembershipGroup from '../../../../hooks/useAlbumMembershipGroup/useAlbumMembershipGroup';
import { isAlbumFolderOwner } from '../../../../../../util/account';
import Angle from '../../../../../../components/global/Angle';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  className: PropTypes.string,
  title: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  backButtonGoesBack: PropTypes.bool,
  backButtonProps: PropTypes.object,
};

const AlbumFolderInnerPageHeader = ({
  albumFolder,
  className,
  title,
  icon,
  user,
  backButtonGoesBack = false,
  backButtonProps,
}) => {
  const history = useHistory();
  const isOwner = isAlbumFolderOwner(user, albumFolder);
  const { albumMembershipGroup } = useAlbumMembershipGroup({
    entityId: albumFolder?.id,
    queryOpts: {
      skip: !albumFolder?.id || isOwner,
    },
  });

  const isNativeWebView = useIsNativeWebView();
  const basename = useBasename();

  if (!albumFolder || isNativeWebView) {
    // For WebViews, we'll be using Helmets with titles.
    return null;
  }

  const albumFolderUrl = (() => {
    if (albumMembershipGroup?.id && !isOwner) {
      return `/app/${basename}/hierarchy-items/${albumFolder.id}`;
    }

    return `/app/${basename}/folders/${albumFolder.id}`;
  })();

  const backButton = (
    <Button
      to={
        !backButtonGoesBack || history.length === 1 ? albumFolderUrl : undefined
      }
      onClick={
        backButtonGoesBack && history.length > 1
          ? () => {
              history.goBack();
            }
          : undefined
      }
      btnStyle="link"
      className="btn-body-back navigation-back-btn"
      {...(backButtonProps ?? {})}
    >
      <Angle direction="left" className="mr-1 navigation-back-icon" />
    </Button>
  );

  const classes = classNames({
    'px-0': true,
    'mb-2': true,
    [className]: !!className,
  });

  return (
    <div className={classes}>
      <div className="d-flex w-100 ">
        {backButton}
        {title && (
          <ProLayout.MainHeader className={classes}>
            <ProLayout.Title title={title} icon={icon} />
          </ProLayout.MainHeader>
        )}
      </div>
    </div>
  );
};

AlbumFolderInnerPageHeader.propTypes = propTypes;

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me, loading }, ownProps }) => ({
    loading: ownProps.loading || loading,
    user: me,
  }),
});

const withAlbumFolder = graphql(AlbumFolderForContributor, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId || match.params.folderId,
    },
    fetchPolicy: 'cache-first',
  }),
  skip: ({ match }) => {
    return !(match.params?.albumFolderId || match.params?.folderId);
  },
  props: ({ data: { albumFolder } }) => ({
    albumFolder,
  }),
});

export default compose(
  withRouter,
  withMeQuery,
  withAlbumFolder,
)(AlbumFolderInnerPageHeader);
